import { api, apiSchemas } from '@verifime/api-definition';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { failureReason, reportIgnoredItem, reportLookupFailure } from './workflowMetadataReporting';

type TWorkflowMetadata = z.infer<typeof apiSchemas.WorkflowMetadata>;

/** Only the signup association type is currently supported. */
const SUPPORTED_ASSOCIATION_TYPE = 'TRACKING_REFERENCE_SIGNUP';

/** What a lookup for one tracking reference came to, bar whether it has settled. */
interface LookupOutcome {
  metadata: TWorkflowMetadata | undefined;
  organisationName: string | undefined;
  organisationId: string | undefined;
  error: boolean;
  notFound: boolean;
}

/** The outcome of the request for one tracking reference, kept with the reference it answers. */
interface SettledLookup {
  trackingReference: string;
  outcome: LookupOutcome;
}

/** What the hook reports while there is no settled outcome for the current tracking reference. */
const NO_OUTCOME: LookupOutcome = {
  metadata: undefined,
  organisationName: undefined,
  organisationId: undefined,
  error: false,
  notFound: false,
};

interface UseWorkflowMetadataByTrackingReferenceResult {
  metadata: TWorkflowMetadata | undefined;
  organisationName: string | undefined;
  /** The client behind the tracking reference, so a caller can match its permission requests. */
  organisationId: string | undefined;
  isLoading: boolean;
  error: boolean;
  /**
   * The request succeeded but gave nothing to read a provider from (no item, or an item of an
   * association type this hook does not support): told apart from `error` (the request failed).
   */
  notFound: boolean;
}

/**
 * Hook that fetches workflow metadata using a tracking reference (from signup email link).
 *
 * This hook is designed for the signup flow where a tracking reference is provided via email.
 * The API returns a single metadata item, so no sorting or filtering is required.
 *
 * @param trackingReference - The tracking reference from the signup email
 */
export function useWorkflowMetadataByTrackingReference(
  trackingReference: string | null | undefined,
): UseWorkflowMetadataByTrackingReferenceResult {
  const [settled, setSettled] = useState<SettledLookup | undefined>(undefined);

  useEffect(() => {
    if (!trackingReference) {
      setSettled(undefined);
      return;
    }

    // A response for a tracking reference that is no longer current is dropped.
    let isCurrent = true;

    /** Records how this reference's lookup came out, unless the reference has moved on. */
    const settle = (outcome: LookupOutcome) => {
      if (isCurrent) {
        setSettled({ trackingReference, outcome });
      }
    };

    /** Records a failed lookup, and reports why. Only a reason is reported, never the error. */
    const settleAsFailed = (reason: string) => {
      if (isCurrent) {
        reportLookupFailure('tracking-reference', reason);
      }
      settle({ ...NO_OUTCOME, error: true });
    };

    /** An item the hook does not read is not a failure, but it is not "found nothing" either. */
    const settleAsIgnoredItem = (associationType: string) => {
      if (isCurrent) {
        reportIgnoredItem('tracking-reference', associationType);
      }
      settle({ ...NO_OUTCOME, notFound: true });
    };

    const settleFromResponse = (data: z.infer<typeof apiSchemas.WorkflowMetadataPageResponse>) => {
      if (!data.itemList) {
        settleAsFailed('invalid-response');
        return;
      }
      const item = data.itemList[0];
      if (!item) {
        settle({ ...NO_OUTCOME, notFound: true });
      } else if (item.associationType !== SUPPORTED_ASSOCIATION_TYPE) {
        settleAsIgnoredItem(item.associationType);
      } else {
        settle({
          ...NO_OUTCOME,
          metadata: item.metadata,
          organisationName: item.organisationName,
          organisationId: item.organisationId,
        });
      }
    };

    api
      .getV1workflowmetadatatrackingTrackingReference({
        params: { trackingReference },
      })
      .then(
        settleFromResponse,
        // The request itself failed.
        (err) => settleAsFailed(failureReason(err)),
      )
      // Reading a response that arrived is the only thing left to throw, and it is a defect here,
      // not a failed request: it is told apart in the log.
      .catch(() => settleAsFailed('unexpected-error'));

    return () => {
      isCurrent = false;
    };
  }, [trackingReference]);

  // Loading is derived from the request state, not set in an effect: it is true from the first
  // render that has a tracking reference until that reference's response lands, and only an outcome
  // for the current reference is read. A caller therefore never reads the render before the fetch
  // starts as a settled, empty result, and a stale organisationId can never scope its data.
  const outcome =
    settled && settled.trackingReference === trackingReference ? settled.outcome : undefined;

  return { ...(outcome ?? NO_OUTCOME), isLoading: !!trackingReference && !outcome };
}
